import { apiPost, getAuthToken } from "../apiUtils";
import { config } from "../../../config";

/**
 * Two modes selected by which fields are present:
 *   xml (with optional pngBase64) → write the current diagram XML in place
 *   pngBase64 (no xml)            → write the current diagram PNG in place
 * Writes go to the project's current files; the next TACO turn seals a version.
 */
export interface SaveDiagramRequest {
  projectId: string;
  xml?: string;
  pngBase64?: string;
}

export interface SaveDiagramOptions {
  /** Use a keepalive fetch (request survives page unload). Bypasses the
   *  Amplify wrapper since it doesn't expose this fetch flag. Use for
   *  unmount and visibility-hidden flushes. */
  keepalive?: boolean;
}

export type SaveDiagramOutcome = "noop" | "written";

export interface SaveDiagramResponseData {
  outcome: SaveDiagramOutcome;
}

export interface SaveDiagramResponse {
  success: boolean;
  data: SaveDiagramResponseData;
}

function buildBody(req: SaveDiagramRequest): Record<string, unknown> {
  const body: Record<string, unknown> = {};
  if (req.xml !== undefined) body.xml = req.xml;
  if (req.pngBase64 !== undefined) body.pngBase64 = req.pngBase64;
  return body;
}

export const saveDiagram = async (
  req: SaveDiagramRequest,
  options?: SaveDiagramOptions,
): Promise<SaveDiagramResponseData> => {
  if (options?.keepalive) {
    return saveDiagramViaKeepaliveFetch(req);
  }
  const restOperation = apiPost(
    `/projects/${req.projectId}/diagram`,
    buildBody(req),
  );
  const { body } = await (await restOperation).response;
  const parsed = (await body.json()) as unknown as SaveDiagramResponse;
  if (!parsed.success || !parsed.data) {
    throw new Error("saveDiagram: unexpected response shape");
  }
  return parsed.data;
};

/** Direct fetch path — needed because @aws-amplify/api's post doesn't
 *  expose the keepalive flag. */
async function saveDiagramViaKeepaliveFetch(
  req: SaveDiagramRequest,
): Promise<SaveDiagramResponseData> {
  const token = await getAuthToken();
  const apiGatewayConfig = config.getApiGatewayConfig();
  const url = `${apiGatewayConfig.url.replace(/\/$/, "")}/projects/${req.projectId}/diagram`;
  const response = await fetch(url, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify(buildBody(req)),
    keepalive: true,
  });
  if (!response.ok) {
    throw new Error(`saveDiagram: HTTP ${response.status}`);
  }
  const parsed = (await response.json()) as SaveDiagramResponse;
  if (!parsed.success || !parsed.data) {
    throw new Error("saveDiagram: unexpected response shape");
  }
  return parsed.data;
}
